/**
 * Slurp.gg token launch — Meteora DAMM v2 single-sided launch pool.
 *
 * Flow:
 *   1. Create SPL mint + ATA + full supply minted to creator
 *   2. Attach Metaplex Token Metadata
 *   3. Vanity mint gen ensures mint < NATIVE_MINT so mint sorts as token A.
 *      With token A = the meme token, CollectFeeMode.OnlyB gives creator
 *      pure-SOL fees (no token-side fees leak through).
 *   4. Create DAMM v2 single-sided pool via createCustomPool:
 *      - flat 1% trading fee
 *      - CollectFeeMode.OnlyB (SOL only)
 *      - isLockLiquidity: true (LP permanent)
 *      - single-sided: all supply at launch price, no SOL from creator
 *      - activationPoint: null (immediate trading)
 *   5. Revoke mint authority
 *   6. Optional dev buy via DAMM v2 swap
 *   7. Post launch receipt to backend indexer
 */

/* global BigInt */
import {
  Keypair,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
  LAMPORTS_PER_SOL,
  ComputeBudgetProgram,
} from "@solana/web3.js";
import {
  createInitializeMintInstruction,
  createAssociatedTokenAccountIdempotentInstruction,
  createMintToInstruction,
  createSetAuthorityInstruction,
  createSyncNativeInstruction,
  createTransferCheckedInstruction,
  AuthorityType,
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  MINT_SIZE,
  getMinimumBalanceForRentExemptMint,
  NATIVE_MINT,
} from "@solana/spl-token";
import BN from "bn.js";
import { slurpApi } from "./slurpApi";
import { sendTx, sendTxBundle } from "./sendTx";
import {
  createCreateMetadataV3Instruction,
  deriveMetadataPda,
} from "./metaplex";

const API_BASE =
  process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";

// Solana's official SPL Memo program. Every launch tx writes a short
// `slurp:v1:launch:<mint>` memo on-chain so explorers / Dune / DexScreener
// can identify slurp launches without us handing them a wallet allowlist.
// The memo signs with the user's existing signature (no extra popup) and
// adds ~60 bytes to the fee-split tx (plenty of headroom under 1232).
const MEMO_PROGRAM_ID = new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr");

// DAMM v2 mainnet program — deployed Oct 2025.
export const DAMM_V2_PROGRAM = new PublicKey(
  "cpamdpZCGKUy5JxQXB4dcpGPiikHawvSWAd6mEn1sGG"
);

const TOKEN_DECIMALS = 6;
const FLAT_FEE_BPS = 100; // 1% flat trading fee

/**
 * Slurp platform treasury — receives 50% of trading fees via a second LP
 * position at launch time (the other 50% position's feeOwner is the creator).
 * Public-key only, never the secret. Secret lives off-box for claiming.
 */
export const SLURP_TREASURY = "FaDBjsBmzbGAMYUPKacNbm53Fe4ZoEfJMZVhuU4YHR8J";

// Dedicated burn-only wallet — receives burn-mode tokens' creator-side
// position NFT at launch so the backend auto-burn worker can claim and
// burn fees on a 15-min cadence. Holds nothing else; never used for
// the protocol's claim path. Address ends in "bUrN" so it's identifiable
// on-chain.
export const BURN_WALLET = "99xHnv277o5JXjwLB9iZxFg2eLur3h2HoRdEk9sbUrN";

// LP-compound wallet — receives lp_compound-mode tokens' creator-side
// position NFT at launch. Backend worker claims SOL fees, swaps half into
// the token via the same pool, then addLiquidity back to the same position.
// Pool reserves grow forever, creator earns zero SOL. Address ends in
// "groW" so it's identifiable on-chain.
export const LP_GROW_WALLET = "dvqdjX55xiiHjWfs6G1KA2BsK5KyLLV4LkfHULZgroW";

// Load the cp-amm-sdk dynamically so webpack chunks this heavy dep only
// when a user actually launches.
const loadCpAmm = async () => {
  const mod = await import("@meteora-ag/cp-amm-sdk");
  return mod;
};

/**
 * Generate a mint keypair whose pubkey sorts BEFORE NATIVE_MINT lex-wise.
 * This guarantees the meme token ends up as token A in the DAMM v2 pool,
 * which in turn means CollectFeeMode.OnlyB = fees in SOL (token B = SOL).
 * Takes ~2 iterations on average since ~50% of random keys sort before 'S'.
 */
function generateVanityMint() {
  let kp = Keypair.generate();
  while (kp.publicKey.toBuffer().compare(NATIVE_MINT.toBuffer()) >= 0) {
    kp = Keypair.generate();
  }
  return kp;
}

/**
 * Main entrypoint. Returns { mint, pool, position, positionNft, signatures }.
 *
 * @param {object} args
 * @param {Connection} args.connection
 * @param {object} args.wallet
 * @param {string} args.name
 * @param {string} args.symbol
 * @param {number} args.supplyWhole
 * @param {number} args.startingMcapUsd
 * @param {number} args.solUsd
 * @param {number} [args.devBuySol=0]
 * @param {string} [args.imageUrl]
 * @param {string} [args.description]
 * @param {string} [args.website]
 * @param {string} [args.twitter]
 * @param {string} [args.telegram]
 * @param {(stage: string, detail?: object) => void} [args.onStage]
 */
export async function launchToken({
  connection,
  wallet,
  name,
  symbol,
  supplyWhole,
  startingMcapUsd,
  solUsd,
  devBuySol = 0,
  imageUrl,
  description,
  website,
  twitter,
  telegram,
  feeMode = "claim",
  notifyFollowers = false,
  onStage = () => {},
}) {
  if (!wallet?.publicKey) throw new Error("Wallet not connected");
  if (!wallet.signTransaction) throw new Error("Wallet doesn't support signing");

  const {
    CpAmm,
    getSqrtPriceFromPrice,
    getBaseFeeParams,
    MAX_SQRT_PRICE,
    deriveTokenVaultAddress,
    derivePositionAddress,
    derivePositionNftAccount,
    SPLIT_POSITION_DENOMINATOR,
  } = await loadCpAmm();

  const owner = wallet.publicKey;

  // ──────────────────────────────────────────────────────────────────
  // 1. Mint + supply + Metaplex metadata (bundled into one tx)
  // ──────────────────────────────────────────────────────────────────
  onStage("creating-mint");
  const mintKp = generateVanityMint();
  const mint = mintKp.publicKey;
  const lamportsForMint = await getMinimumBalanceForRentExemptMint(connection);
  const creatorAta = getAssociatedTokenAddressSync(mint, owner);
  const supplyRaw = new BN(supplyWhole).mul(
    new BN(10).pow(new BN(TOKEN_DECIMALS))
  );

  const metadataPda = deriveMetadataPda(mint);
  const metadataUri = `${API_BASE}/api/metadata/${mint.toBase58()}`;

  // Single tx: create mint, ATA, mint full supply, attach Metaplex, revoke
  // mint authority. After this, supply is permanently locked at 1B.
  const mintTx = new Transaction().add(
    SystemProgram.createAccount({
      fromPubkey: owner,
      newAccountPubkey: mint,
      lamports: lamportsForMint,
      space: MINT_SIZE,
      programId: TOKEN_PROGRAM_ID,
    }),
    createInitializeMintInstruction(mint, TOKEN_DECIMALS, owner, null),
    createAssociatedTokenAccountIdempotentInstruction(owner, creatorAta, owner, mint),
    createMintToInstruction(mint, creatorAta, owner, BigInt(supplyRaw.toString())),
    createCreateMetadataV3Instruction({
      metadata: metadataPda,
      mint,
      mintAuthority: owner,
      payer: owner,
      updateAuthority: owner,
      name: (name ?? "").slice(0, 32),
      symbol: (symbol ?? "").slice(0, 10),
      uri: metadataUri.slice(0, 200),
      sellerFeeBasisPoints: 0,
    }),
    // Revoke mint authority last — after all minting is done. Supply locked.
    createSetAuthorityInstruction(mint, owner, AuthorityType.MintTokens, null)
  );
  onStage("creating-mint");
  const mintSig = await sendTx(connection, wallet, mintTx, [mintKp], {
    cuLimit: 600_000,
  });
  onStage("mint-created", { mint: mint.toBase58(), sig: mintSig });

  // ──────────────────────────────────────────────────────────────────
  // 2. Create DAMM v2 single-sided pool
  // ──────────────────────────────────────────────────────────────────
  onStage("creating-pool");
  const cpAmm = new CpAmm(connection);

  // tokenA = our meme token (ensured via vanity gen); tokenB = SOL.
  const tokenAMint = mint;
  const tokenBMint = NATIVE_MINT;

  // Price math: SOL per whole token = starting_mcap_usd / sol_usd / supply
  const initPriceSol = startingMcapUsd / solUsd / supplyWhole;
  const initSqrtPrice = getSqrtPriceFromPrice(
    initPriceSol.toFixed(18),
    TOKEN_DECIMALS,
    9 // SOL decimals
  );

  // Single-sided launch: initPrice is the minimum, range extends to max.
  const sqrtMinPrice = initSqrtPrice;
  const sqrtMaxPrice = MAX_SQRT_PRICE;
  const liquidityDelta = cpAmm.preparePoolCreationSingleSide({
    tokenAAmount: supplyRaw,
    minSqrtPrice: sqrtMinPrice,
    maxSqrtPrice: sqrtMaxPrice,
    initSqrtPrice,
    collectFeeMode: 1, // OnlyB → fees in SOL only
  });

  // Flat 1% fee: scheduler with startingFee == endingFee = no decay.
  const baseFee = getBaseFeeParams(
    {
      baseFeeMode: 0, // FeeTimeSchedulerLinear (degenerates to flat when start==end)
      feeTimeSchedulerParam: {
        startingFeeBps: FLAT_FEE_BPS,
        endingFeeBps: FLAT_FEE_BPS,
        numberOfPeriod: 0,
        totalDuration: 0,
      },
    },
    9, // quote decimals
    1 // ActivationType.Timestamp
  );

  const positionNftKp = Keypair.generate();
  const {
    tx: createPoolTx,
    pool: poolPda,
    position: positionPda,
  } = await cpAmm.createCustomPool({
    payer: owner,
    creator: owner,
    positionNft: positionNftKp.publicKey,
    tokenAMint,
    tokenBMint,
    tokenAAmount: supplyRaw,
    tokenBAmount: new BN(0),
    sqrtMinPrice,
    sqrtMaxPrice,
    liquidityDelta,
    initSqrtPrice,
    poolFees: {
      baseFee,
      compoundingFeeBps: 0,
      padding: 0,
      dynamicFee: null,
    },
    hasAlphaVault: false,
    activationType: 1, // Timestamp
    collectFeeMode: 1, // OnlyB
    activationPoint: null, // immediate
    tokenAProgram: TOKEN_PROGRAM_ID,
    tokenBProgram: TOKEN_PROGRAM_ID,
    isLockLiquidity: true, // LP locked forever
  });

  // ──────────────────────────────────────────────────────────────────
  // 3. Bundle pool creation + dev buy into ONE atomic tx — bot-proof.
  //    Trick: cpAmm.swap() normally fetches poolState from RPC (fails since
  //    pool doesn't exist yet). We pass a minimal fake poolState with just
  //    the baseFeeMode byte set (SDK only uses this for rate-limiter path,
  //    which we don't enable). Everything else in the swap uses params we
  //    pass explicitly.
  // ──────────────────────────────────────────────────────────────────
  let devBuySig;
  if (devBuySol > 0) {
    const lamports = new BN(Math.floor(devBuySol * LAMPORTS_PER_SOL));
    const wsolAta = getAssociatedTokenAddressSync(NATIVE_MINT, owner);

    // Derive vault PDAs without fetching — pool will exist by the time the
    // swap ix runs within the same tx.
    const tokenAVault = deriveTokenVaultAddress(tokenAMint, poolPda);
    const tokenBVault = deriveTokenVaultAddress(tokenBMint, poolPda);

    // Minimal fake poolState: the SDK reads data[8] for baseFeeMode. We
    // launch with FeeTimeSchedulerLinear (=0), so a 16-byte zero buffer
    // satisfies the check without triggering rate-limiter logic.
    const fakePoolState = {
      poolFees: { baseFee: { baseFeeInfo: { data: Buffer.alloc(16, 0) } } },
      activationType: 1,
    };

    // Slurp host-fee ATA — 20% of Meteora's protocol fee gets routed here
    // when we pass it as `referralTokenAccount`. Free revenue, opt-in per
    // swap. See solanaSwap.js for the long explainer.
    const slurpHostAta = getAssociatedTokenAddressSync(
      NATIVE_MINT,
      new PublicKey(SLURP_TREASURY),
      true,
    );
    const swapTx = await cpAmm.swap({
      payer: owner,
      pool: poolPda,
      inputTokenMint: NATIVE_MINT,
      outputTokenMint: mint,
      amountIn: lamports,
      minimumAmountOut: new BN(1), // atomic → no sandwich possible → 1 is safe
      tokenAMint,
      tokenBMint,
      tokenAVault,
      tokenBVault,
      tokenAProgram: TOKEN_PROGRAM_ID,
      tokenBProgram: TOKEN_PROGRAM_ID,
      referralTokenAccount: slurpHostAta,
      poolState: fakePoolState,
    });

    // Append wrap-SOL + slurp host ATA create + swap ixs onto the pool
    // creation tx. All in one tx, one signature, no gap between
    // pool-goes-live and dev-buy.
    createPoolTx.instructions.push(
      createAssociatedTokenAccountIdempotentInstruction(owner, wsolAta, owner, NATIVE_MINT),
      // Idempotent so subsequent launches don't double-create.
      createAssociatedTokenAccountIdempotentInstruction(
        owner,
        slurpHostAta,
        new PublicKey(SLURP_TREASURY),
        NATIVE_MINT,
      ),
      SystemProgram.transfer({
        fromPubkey: owner,
        toPubkey: wsolAta,
        lamports: lamports.toNumber(),
      }),
      createSyncNativeInstruction(wsolAta),
      ...swapTx.instructions.filter(
        (ix) => !ix.programId.equals(ComputeBudgetProgram.programId)
      )
    );
    onStage("dev-buy-bundled");
  }

  // ──────────────────────────────────────────────────────────────────
  // 4b. 50/50 fee split — DAMM v2 `splitPosition2` requires BOTH owners
  //     to sign, so we can't set the slurp treasury as secondOwner (we
  //     don't have its secret). Workaround: split with the creator as
  //     both owners, then SPL-transfer the second position NFT to the
  //     slurp treasury's ATA. Everything signs with the creator's key.
  //
  //     And — critically — we bundle this fee-split tx with the pool
  //     creation tx via `signAllTransactions`. User sees ONE wallet
  //     popup, both txs are signed together, so the creator can't abort
  //     the split after pool goes live.
  // ──────────────────────────────────────────────────────────────────
  const slurpTreasuryPk = new PublicKey(SLURP_TREASURY);
  const slurpPosNftKp = Keypair.generate();
  const slurpPositionPda = derivePositionAddress(slurpPosNftKp.publicKey);
  const creatorOwnedSlurpNftAta = derivePositionNftAccount(slurpPosNftKp.publicKey);
  const creatorPositionNftAccount = derivePositionNftAccount(positionNftKp.publicKey);

  // Position NFTs are Token-2022 mints — derive slurp's ATA under that program.
  const slurpFinalNftAta = getAssociatedTokenAddressSync(
    slurpPosNftKp.publicKey,
    slurpTreasuryPk,
    true, // allowOwnerOffCurve — treasury could be any pubkey
    TOKEN_2022_PROGRAM_ID
  );

  const createSlurpPosTx = await cpAmm.createPosition({
    owner: owner, // creator holds the NFT temporarily
    payer: owner,
    pool: poolPda,
    positionNft: slurpPosNftKp.publicKey,
  });
  // 62.5/37.5 LP split — slurp keeps 62.5% of LP fees, creator gets 37.5%.
  // Math (per $1 trade): Meteora $0.20 off the top → $0.80 LP. Creator
  // (37.5% liq) = $0.30. Slurp (62.5% liq) = $0.50. Slurp's $0.50
  // internally splits 60/20/20 into slurp keep ($0.30) / leaderboard
  // ($0.10) / monthly jackpot ($0.10). Marketing: post-Meteora the LP
  // fee splits 37.5/37.5/12.5/12.5 across creator/slurp/leaderboard/jackpot.
  const splitTx = await cpAmm.splitPosition2({
    firstPositionOwner: owner,
    secondPositionOwner: owner,
    pool: poolPda,
    firstPosition: positionPda,           // creator-side position (37.5% LP)
    firstPositionNftAccount: creatorPositionNftAccount,
    secondPosition: slurpPositionPda,     // slurp-side position (62.5% LP)
    secondPositionNftAccount: creatorOwnedSlurpNftAta,
    numerator: Math.floor(SPLIT_POSITION_DENOMINATOR * 0.625),
  });
  // Slurp launch marker — placed first in the tx so indexers reading the
  // memo program logs see it before parsing the rest of the ix list.
  // Same signer as the rest of the tx, no extra popup, no extra accounts.
  const slurpMemoIx = new TransactionInstruction({
    keys: [{ pubkey: owner, isSigner: true, isWritable: false }],
    programId: MEMO_PROGRAM_ID,
    data: Buffer.from(`slurp:v1:launch:${mint.toBase58()}`, "utf8"),
  });
  const feeSplitTx = new Transaction();
  feeSplitTx.add(
    slurpMemoIx,
    ...createSlurpPosTx.instructions,
    ...splitTx.instructions.filter(
      (ix) => !ix.programId.equals(ComputeBudgetProgram.programId)
    ),
    // Create slurp's Token-2022 ATA for the NFT mint (idempotent).
    createAssociatedTokenAccountIdempotentInstruction(
      owner,
      slurpFinalNftAta,
      slurpTreasuryPk,
      slurpPosNftKp.publicKey,
      TOKEN_2022_PROGRAM_ID
    ),
    // Hand the position NFT to slurp (1 unit, 0 decimals).
    createTransferCheckedInstruction(
      creatorOwnedSlurpNftAta,
      slurpPosNftKp.publicKey,
      slurpFinalNftAta,
      owner,
      1,
      0,
      [],
      TOKEN_2022_PROGRAM_ID
    )
  );

  // Mode-specific hand-off — transfer the CREATOR-side position NFT to a
  // dedicated worker wallet so the backend takes over fee handling. After
  // this, the creator can never claim those fees themselves.
  //   burn        → BURN_WALLET (auto-burn every 15 min)
  //   lp_compound → LP_GROW_WALLET (auto-compound every 15 min)
  //   claim       → no transfer, creator keeps the NFT
  // This block is appended to the same tx so it's atomic with the rest of
  // the launch — single signature popup.
  const handoffPubkey =
    feeMode === "burn" ? BURN_WALLET :
    feeMode === "lp_compound" ? LP_GROW_WALLET :
    null;
  if (handoffPubkey) {
    const targetPk = new PublicKey(handoffPubkey);
    const targetNftAta = getAssociatedTokenAddressSync(
      positionNftKp.publicKey,
      targetPk,
      true, // allowOwnerOffCurve
      TOKEN_2022_PROGRAM_ID
    );
    feeSplitTx.add(
      // Create the target wallet's ATA for the creator-side position NFT.
      createAssociatedTokenAccountIdempotentInstruction(
        owner,
        targetNftAta,
        targetPk,
        positionNftKp.publicKey,
        TOKEN_2022_PROGRAM_ID
      ),
      // Hand it over.
      createTransferCheckedInstruction(
        creatorPositionNftAccount,
        positionNftKp.publicKey,
        targetNftAta,
        owner,
        1,
        0,
        [],
        TOKEN_2022_PROGRAM_ID
      )
    );
  }

  // Send both txs in one wallet popup. Same blockhash, same session, no
  // way for the user to bail between pool creation and fee split. Both
  // are mandatory — if the split reverts the bundle throws, so a creator
  // can never end up with 100% of the LP by underfunding tx 2.
  onStage("pool-and-fees");
  const bundleResults = await sendTxBundle(connection, wallet, [
    {
      tx: createPoolTx,
      extraSigners: [positionNftKp],
      cuLimit: devBuySol > 0 ? 1_200_000 : 800_000,
    },
    {
      tx: feeSplitTx,
      extraSigners: [slurpPosNftKp],
      cuLimit: 500_000,
    },
  ]);
  const createPoolSig = bundleResults[0].sig;
  const splitResult = bundleResults[1];
  onStage("pool-created", { pool: poolPda.toBase58(), sig: createPoolSig });
  if (devBuySol > 0) {
    devBuySig = createPoolSig;
    onStage("dev-buy-complete", { sig: createPoolSig });
  }
  onStage("fee-split-done", {
    slurp_position: slurpPositionPda.toBase58(),
    slurp_position_nft: slurpPosNftKp.publicKey.toBase58(),
    sig: splitResult.sig,
  });

  // ──────────────────────────────────────────────────────────────────
  // 5. Post launch receipt to backend
  // ──────────────────────────────────────────────────────────────────
  // Receipt POST — this is what makes the token appear on the site. We retry
  // up to 5× because the on-chain launch is already done; the backend just
  // needs to catch up. If ALL retries fail, we return the launch anyway but
  // flag `receiptPosted: false` so the UI can tell the user "it's on-chain,
  // backend sync pending".
  const blank = (v) => (typeof v === "string" && v.trim() === "" ? null : v ?? null);
  // Users often type bare domains (fries.food). Zod .url() needs a scheme,
  // so prefix https:// when missing. Anything that's already a full URL
  // (http://, https://, tg://, mailto:) passes through unchanged.
  const normalizeUrl = (v) => {
    const s = blank(v);
    if (!s) return null;
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(s) ? s : `https://${s}`;
  };
  const receiptBody = {
    mint: mint.toBase58(),
    pool: poolPda.toBase58(),
    position: positionPda.toBase58(),
    position_nft: positionNftKp.publicKey.toBase58(),
    slurp_position: slurpPositionPda.toBase58(),
    slurp_position_nft: slurpPosNftKp.publicKey.toBase58(),
    creator: owner.toBase58(),
    name,
    symbol,
    image_url: blank(imageUrl),
    description: blank(description),
    website: normalizeUrl(website),
    twitter: normalizeUrl(twitter),
    telegram: normalizeUrl(telegram),
    decimals: TOKEN_DECIMALS,
    supply_whole: supplyWhole,
    token_is_x: true, // vanity mint guarantees this
    bin_step: 0, // not applicable for DAMM v2
    swap_fee_bps: FLAT_FEE_BPS,
    starting_mcap_usd: startingMcapUsd,
    launch_tx: createPoolSig,
    fee_mode:
      feeMode === "burn" ? "burn" :
      feeMode === "lp_compound" ? "lp_compound" :
      "claim",
    notify_followers: !!notifyFollowers,
  };

  // First attempt is blocking — on a happy path the receipt POST returns
  // in ~200ms-1s so this is invisible to the user. If it fails, we kick off
  // background retries and let the UI flip to "Launched!" immediately. The
  // on-chain launch is already real and observable by the user; the receipt
  // is just our DB sync. Old behavior blocked for up to ~65s on failures.
  let receiptPosted = false;
  let lastReceiptErr = null;
  try {
    await slurpApi.postLaunch(receiptBody);
    receiptPosted = true;
  } catch (err) {
    lastReceiptErr = err;
    // 3 background retries with 1.5s/3s/4.5s backoff. UI flips to done
    // while these run. We fire onStage events on success/failure so the
    // caller can show a "Synced!" toast or a "Sync pending — retrying"
    // hint without blocking the main launch flow.
    void (async () => {
      for (let i = 0; i < 3; i++) {
        await new Promise((r) => setTimeout(r, 1500 * (i + 1)));
        try {
          await slurpApi.postLaunch(receiptBody);
          onStage("receipt-synced", { attempt: i + 2 });
          return;
        } catch (e) {
          lastReceiptErr = e;
        }
      }
      onStage("receipt-failed", { error: String(lastReceiptErr) });
      console.warn(`[launch] receipt POST exhausted retries:`, lastReceiptErr);
    })();
  }

  onStage("done", { mint: mint.toBase58(), pool: poolPda.toBase58() });
  return {
    mint: mint.toBase58(),
    pool: poolPda.toBase58(),
    position: positionPda.toBase58(),
    positionNft: positionNftKp.publicKey.toBase58(),
    receiptPosted, // false here means retries are running in background
    signatures: {
      mint: mintSig, // also contains revoke (bundled into mint tx)
      pool: createPoolSig, // also contains dev buy if enabled (atomic)
      devBuy: devBuySig,
    },
  };
}
